import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import GoldRule from '../components/GoldRule';
import useProducts from '../hooks/useProducts';
import useProductImages, { cleanImage } from '../hooks/useProductImages';
import { PENDING_PIECES, getSetBySlug, getSetHero } from '../data/products';
import LeatherBackdrop from '../components/LeatherBackdrop';
import { Reveal, RevealLines } from '../components/motion/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = [3, 5, 7];
const FREE_HAND_MAX = 12;

export default function MyMrBoss() {
  const { visibleProducts, sets } = useProducts();
  const { manifest } = useProductImages();
  const set = sets.find((s) => s.custom) || getSetBySlug('my-mr-boss');
  const [cfg, setCfg] = useState(null);
  const [size, setSize] = useState(null); // null = free hand
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const pieces = useMemo(() => {
    const catalogue = Object.values(visibleProducts).map((p) => ({
      key: p.code,
      name: p.name,
      meta: p.code,
      category: p.category,
      price: p.price ?? null,
      image: cleanImage(manifest, p.code),
      pending: false,
    }));
    const upcoming = Object.entries(PENDING_PIECES).map(([key, p]) => ({
      key,
      name: p.name,
      meta: 'Awaiting photography',
      category: p.category,
      price: null,
      image: null,
      pending: true,
    }));
    return [...catalogue, ...upcoming];
  }, [visibleProducts, manifest]);

  const limit = size || FREE_HAND_MAX;
  const full = picked.length >= limit;
  const tierPrice = size ? Number(cfg?.tier_prices?.[String(size)] || 0) : 0;
  const currency = (cfg?.currency || 'gbp').toUpperCase();
  const defaultPrice = Number(cfg?.default_product_price || 0);

  const pickedPieces = picked.map((k) => pieces.find((p) => p.key === k)).filter(Boolean);
  const anyPending = pickedPieces.some((p) => p.pending);
  const priced = pickedPieces.every((p) => Number(p.price ?? defaultPrice) > 0);
  const sum = pickedPieces.reduce((t, p) => t + Number(p.price ?? defaultPrice), 0);
  const total = tierPrice > 0 ? tierPrice : sum;
  const canCheckout =
    picked.length > 0 &&
    !anyPending &&
    (tierPrice > 0 ? picked.length === size : priced && sum > 0);

  const toggle = (key) => {
    setError('');
    setPicked((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= limit) {
        setError(
          size
            ? `This size takes ${size} pieces. Remove one to swap it out.`
            : `Twelve pieces is the maximum for a single set.`
        );
        return prev;
      }
      return [...prev, key];
    });
  };

  const chooseSize = (next) => {
    setSize(next);
    setError('');
    setPicked((prev) => prev.slice(0, next || FREE_HAND_MAX));
  };

  const checkout = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        kind: 'custom',
        target_id: 'my-mr-boss',
        origin_url: window.location.origin,
        items: picked,
        tier: size || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not open checkout. Send it as an enquiry instead.');
      setBusy(false);
    }
  };

  if (!set) return null;

  return (
    <div data-testid="my-mr-boss-page">
      <section className="relative overflow-hidden">
        <LeatherBackdrop variant="grain" intensity={0.34} drift={9} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <Reveal y={12}>
            <div className="eyebrow text-gold mb-8">Build your own</div>
          </Reveal>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-mb leading-[0.95]">
            <RevealLines lines={['My Mr. Boss.']} />
          </h1>
          <Reveal delay={0.26} className="mt-10">
            <GoldRule size="med" />
          </Reveal>
          <Reveal delay={0.32}>
            <p className="mt-10 font-display italic text-[18px] md:text-[20px] text-mb-muted max-w-2xl leading-relaxed">
              {set.description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* EXAMPLE BUILD — a bunch of pieces gathered as inspiration */}
      <section className="hairline-t border-subtle" data-testid="my-mr-boss-example">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14">
          <div className="relative aspect-[16/9] overflow-hidden hairline border-gold bg-mb">
            <img
              src={getSetHero('my-mr-boss')}
              alt="An example My Mr. Boss build"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.opacity = '0'; }}
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(8,9,10,0.85) 0%, transparent 45%)' }}
            />
            <div className="absolute left-6 md:left-10 bottom-6 md:bottom-8">
              <div className="eyebrow text-gold mb-2">An example, not a fixed set</div>
              <div className="font-display italic text-xl md:text-3xl text-mb">
                A few pieces, gathered — yours could look nothing like this.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Size */}
      <section className="hairline-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14">
          <div className="eyebrow text-mb-muted mb-8">First, the size</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SIZES.map((n) => {
              const p = Number(cfg?.tier_prices?.[String(n)] || 0);
              const active = size === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => chooseSize(n)}
                  data-testid={`size-option-${n}`}
                  className={`text-left p-6 hairline transition-colors ${
                    active ? 'border-gold bg-mb-surface' : 'border-subtle hover:border-mb-muted'
                  }`}
                >
                  <div className="font-display text-3xl text-mb">{n} pieces</div>
                  <div className="mt-3 eyebrow-sm text-mb-muted">
                    {p > 0 ? `${currency} ${p.toFixed(2)}` : 'Price on enquiry'}
                  </div>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => chooseSize(null)}
              data-testid="size-option-free"
              className={`text-left p-6 hairline transition-colors ${
                size === null ? 'border-gold bg-mb-surface' : 'border-subtle hover:border-mb-muted'
              }`}
            >
              <div className="font-display text-3xl text-mb">Free hand</div>
              <div className="mt-3 eyebrow-sm text-mb-muted">
                Up to {FREE_HAND_MAX} pieces, priced as you go
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Pieces + summary */}
      <section className="hairline-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-end justify-between mb-8">
              <div className="eyebrow text-mb-muted">Then, the pieces</div>
              <div className="eyebrow-sm text-gold" data-testid="picked-count">
                {picked.length} / {limit} chosen
              </div>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {pieces.map((p) => {
                const on = picked.includes(p.key);
                const blocked = !on && full;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => toggle(p.key)}
                    disabled={blocked}
                    data-testid={`piece-${p.key.toLowerCase().replace(/\s/g, '-')}`}
                    className={`relative text-left hairline transition-colors ${
                      on ? 'border-gold bg-mb-surface' : 'border-subtle hover:border-mb-muted'
                    } ${blocked ? 'opacity-35 cursor-not-allowed' : ''}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-mb">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt={p.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.opacity = 0;
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="eyebrow-sm text-mb-muted px-4 text-center">
                            Awaiting photography
                          </span>
                        </div>
                      )}
                      {on && (
                        <span className="absolute top-3 right-3 w-7 h-7 bg-gold flex items-center justify-center">
                          <Check size={14} strokeWidth={2} className="text-mb" />
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="font-display text-[18px] text-mb">{p.name}</div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="eyebrow-sm text-mb-muted">{p.meta}</span>
                        <span className="eyebrow-sm text-gold">
                          {Number(p.price ?? defaultPrice) > 0
                            ? `${currency} ${Number(p.price ?? defaultPrice).toFixed(2)}`
                            : 'On enquiry'}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary rail */}
          <div className="lg:col-span-4">
            <div
              className="lg:sticky lg:top-[100px] hairline border-gold bg-mb-surface p-8"
              data-testid="build-summary"
            >
              <div className="eyebrow text-gold mb-6">Your set</div>

              {picked.length === 0 ? (
                <p className="text-[14px] text-mb-muted leading-relaxed">
                  Nothing chosen yet. Pick the pieces you want and they will
                  appear here in order.
                </p>
              ) : (
                <ol className="space-y-3">
                  {pickedPieces.map((p, i) => (
                    <li key={p.key} className="grid grid-cols-[26px_1fr] gap-3">
                      <span className="font-display text-[11px] tracking-[0.24em] text-gold pt-1">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="font-display text-[15px] text-mb">{p.name}</span>
                    </li>
                  ))}
                </ol>
              )}

              <div className="mt-8 hairline-t border-subtle pt-6 flex items-end justify-between">
                <div className="eyebrow-sm text-mb-muted">
                  {size ? `${size}-piece set` : `Free hand · max ${FREE_HAND_MAX}`}
                </div>
                <div className="font-display text-[26px] text-mb" data-testid="build-total">
                  {canCheckout ? `${currency} ${total.toFixed(2)}` : 'On enquiry'}
                </div>
              </div>

              {error && (
                <div className="mt-6 hairline border-gold p-4" data-testid="build-error">
                  <p className="text-[13px] text-mb-muted">{error}</p>
                </div>
              )}

              {sent ? (
                <div className="mt-8" data-testid="build-confirmation">
                  <div className="eyebrow text-gold mb-3">Noted.</div>
                  <p className="text-[13px] text-mb-muted leading-relaxed">
                    Your build is with the atelier. A single representative will
                    write to you within one business day with pricing and lead time.
                  </p>
                </div>
              ) : (
                <div className="mt-8 space-y-3">
                  <button
                    type="button"
                    onClick={checkout}
                    disabled={!canCheckout || busy}
                    data-magnetic
                    data-testid="build-checkout-btn"
                    className={`btn-gold w-full justify-center ${
                      !canCheckout || busy ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                  >
                    {busy ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Opening checkout
                      </>
                    ) : (
                      'Check out this set'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSent(picked.length > 0)}
                    disabled={picked.length === 0}
                    data-testid="build-enquire-btn"
                    className={`btn-outline-gold w-full justify-center ${
                      picked.length === 0 ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                  >
                    Send as an enquiry
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPicked([]);
                      setError('');
                    }}
                    data-testid="build-clear-btn"
                    className="eyebrow-sm text-mb-muted hover:text-gold transition-colors w-full text-center pt-2"
                  >
                    Clear the build
                  </button>
                </div>
              )}

              <p className="mt-8 eyebrow-sm text-mb-muted leading-relaxed">
                Pieces still awaiting photography can be included — those builds
                are quoted by the atelier.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="hairline-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 flex flex-wrap items-center justify-between gap-6">
          <div className="font-display italic text-[18px] text-mb-muted">
            Prefer one of the house sets?
          </div>
          <Link to="/sets" className="btn-outline-gold" data-testid="build-to-sets">
            Back to the collection
          </Link>
        </div>
      </section>
    </div>
  );
}
